import { InlineNotification, Modal } from '@carbon/react';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { formatMutationResource } from 'utils/misc';
import { structureGridCollectionsQueryAtom } from './atoms';
import style from './promote-collection-modal.module.scss';

// ─────────────────────────────────────
// Types
// ─────────────────────────────────────

interface PromoteCollectionModalProps {
  isOpen: boolean;
  isPromoted: boolean;
  collectionName: string;
  basin: string;
  isLoading?: boolean;
  error?: string | null;
  /** Validity of the collection — passed through for future use. */
  collectionValidity?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export const PromoteCollectionModal = ({
  isOpen,
  isPromoted,
  collectionName,
  basin,
  isLoading = false,
  error,
  onClose,
  onConfirm,
}: PromoteCollectionModalProps) => {
  const [existingDefaultQueryState, fetchExistingDefault] = useAtom(
    structureGridCollectionsQueryAtom
  );
  const { data: existingDefaultData, loading: existingDefaultLoading } =
    formatMutationResource(existingDefaultQueryState);

  // When the modal opens for promote (not remove), fetch the current default
  // collection for this basin so we can show the right confirmation copy.
  useEffect(() => {
    if (!isOpen || isPromoted) return;
    fetchExistingDefault({
      url: '/queries/structure_grid_collections',
      type: 'POST',
      data: { isDefault: true, basin },
    });
  }, [isOpen, isPromoted, basin, fetchExistingDefault]);

  const existingDefault =
    !isPromoted && existingDefaultData?.results?.[0]
      ? existingDefaultData.results[0]
      : null;

  const heading = isPromoted
    ? 'Remove Promotion Status'
    : existingDefault
      ? 'Promote Collection'
      : 'Set as Default';

  return (
    <Modal
      open={isOpen}
      modalLabel={collectionName}
      modalHeading={heading}
      primaryButtonText={isLoading ? 'Confirming...' : 'Confirm'}
      primaryButtonDisabled={isLoading || existingDefaultLoading}
      secondaryButtonText="Cancel"
      onRequestSubmit={onConfirm}
      onRequestClose={isLoading ? undefined : onClose}
      className={style.modal}
    >
      {error && (
        <InlineNotification kind="error" title={error} hideCloseButton />
      )}
      {isPromoted ? (
        <p className={style.body}>
          Removing the promotion status will return this collection to a
          non-promoted state. Any workflows, views, or assumptions based on its
          promoted status may no longer apply. This action can be reversed.
        </p>
      ) : existingDefault ? (
        <>
          <p className={style.body}>
            Promote Structure Grid Collection: <strong>{collectionName}</strong>
          </p>
          <p className={style.body}>
            This will replace the current promoted structure grid collection{' '}
            <strong>{existingDefault.collectionName}</strong> making this
            collection the vetted reference for the <strong>{basin}</strong>{' '}
            basin and used automatically in downstream workflows.
          </p>
        </>
      ) : (
        <>
          <p className={style.body}>
            Set Default Structure Grid Collection:{' '}
            <strong>{collectionName}</strong>
          </p>
          <p className={style.body}>
            This will set <strong>{collectionName}</strong> as the Default
            Structure Grid Collection for the <strong>{basin}</strong>.
            Structure Grid Collections that have been promoted to Default act as
            the vetted reference and are selected first for use in downstream
            workflows.
          </p>
        </>
      )}
    </Modal>
  );
};
